import React, { useState, useEffect, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL } from "../config";
import {
  Grid,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Button,
  Collapse,
  Modal,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
} from "@mui/material";

const SubscriptionPaper = styled(Paper)(({ theme, tiercolor }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.secondary,
  cursor: "pointer",
  border: `2px solid ${tiercolor}`,
  "&:hover": {
    boxShadow: `0 0 15px ${tiercolor}`,
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

const Subscriptions = ({ user, token }) => {
  const isMountedRef = useRef(true);
  const newWindowRef = useRef(null);
  const urlRef = useRef(null);
  
  // Helper function - define early to avoid initialization errors
  const isLocalhost = () => {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.startsWith('localhost');
  };
  
  const [selectedBool, setSelectedBool] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [selected, setSelected] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [subLength, setSubLength] = useState("monthly");
  const [prices, setPrices] = useState([0.99, 9.99, 19.99, 39.99]);
  const [successMessage, setSuccessMessage] = useState("Congratulations! You are now subscribed to ");
  const [reviseMode, setReviseMode] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [testStripeFlow, setTestStripeFlow] = useState(false); // For testing webhook flow locally

  // Tier configuration - no more hardcoded price IDs needed
  const tierPlans = {
    1: 'starter',
    2: 'basic', 
    3: 'plus',
    4: 'premium',
  };  const tierColors = {
    1: "#ffc107", // starter - yellow
    2: "#007bff", // basic - blue
    3: "#6f42c1", // plus - purple
    4: "#28a745", // premium - green
  };

  const addOnOptions = [
    { label: "+1TB Storage", price: 19.99, id: "storage" }, // Use generic type instead of specific price ID
  ];

  // Price configurations
  const monthlyPrices = [0.99, 9.99, 19.99, 39.99];
  const annualPrices = [9.99, 99.99, 199.99, 399.99];

  useEffect(() => {
    const subscription = user?.subscription;
    if (parseInt(subscription) !== 0) {
      setReviseMode(true);
    }

    // Fetch current subscription data
    fetchCurrentSubscription();

    return () => {
      isMountedRef.current = false;
      if (newWindowRef.current) {
        newWindowRef.current.close();
      }
    };
  }, [user]);

  const fetchCurrentSubscription = async () => {
    try {
      // Ensure we have a valid token
      let authToken = token;
      if (typeof authToken === 'function') {
        authToken = await authToken();
      }
      
      if (!authToken || typeof authToken !== 'string' || authToken === 'undefined') {
        console.error('No valid authentication token found for fetching subscription');
        return;
      }
      
      const response = await fetch(API_URL + "/users/v1/subscribe", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentSubscription(data);
        if (data.subscriptionId) {
          setReviseMode(true);
        }
      } else {
        console.error('Failed to fetch subscription:', response.status, response.statusText);
      }
    } catch (error) {
      console.error("Error fetching current subscription:", error);
    }
  };

  const checkIfClosed = () => {
    if (newWindowRef.current) {
      try {
        console.log(newWindowRef.current.postMessage("onApprove", urlRef.current));
      } catch (err) {
        console.log("CORB");
      }

      if (newWindowRef.current.closed) {
        setSuccessMessage(
          "You have attempted to update your subscription. If successful, your new subscription will be reflected next billing cycle."
        );
        setRedirect(true);
      } else {
        setTimeout(checkIfClosed, 500);
      }
    }
  };

  const popupCenter = ({ url, title, w, h }) => {
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : window.screen.width;
    const height = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : window.screen.height;

    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft;
    const top = (height - h) / 2 / systemZoom + dualScreenTop;
    
    newWindowRef.current = window.open(
      url,
      title,
      `scrollbars=yes,width=${w / systemZoom},height=${h / systemZoom},top=${top},left=${left}`
    );

    if (newWindowRef.current?.focus) {
      newWindowRef.current.focus();
    }
  };

  const changeSubscription = async () => {
    setSubscribing(true);

    try {
      // Ensure we have a valid token
      let authToken = token;
      if (typeof authToken === 'function') {
        authToken = await authToken();
      }
      
      if (!authToken || typeof authToken !== 'string' || authToken === 'undefined') {
        toast.error('No valid authentication token found. Please log in again.');
        setSubscribing(false);
        return;
      }

      // For localhost with no test flow, simulate without API call
      if (isLocalhost() && !testStripeFlow) {
        toast.success("Development mode: Subscription change approved!");
        setTimeout(() => {
          setSuccessMessage("Development subscription changed successfully!");
          setRedirect(true);
        }, 2000);
        return;
      }

      // If we have an existing subscription, try to update it
      if (currentSubscription?.subscriptionId && selectedId > 0) {
        const requestBody = {
          type: "update_subscription",
          subscriptionId: currentSubscription.subscriptionId,
          tier: selectedId,
          interval: subLength === "monthly" ? "monthly" : "annual",
          plan: tierPlans[selectedId],
        };

        // Add test mode flag for local development
        if (isLocalhost() && testStripeFlow) {
          requestBody.testStripeFlow = true;
        }

        const response = await fetch(API_URL + "/users/v1/subscribe", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          toast.success("Subscription updated successfully!");
          setTimeout(() => {
            setSuccessMessage("Your subscription has been updated!");
            setRedirect(true);
          }, 2000);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || errorData.error || 'Update failed');
        }
      } else {
        // No existing subscription or no tier selected, redirect to new subscription flow
        toast.info("Creating new subscription...");
        await handleStripeCheckout();
      }
    } catch (error) {
      console.error('Subscription update error:', error);
      toast.error(`Failed to update subscription: ${error.message}`);
      setSubscribing(false);
    }
  };

  const handleSubscription = (tier, tierId) => {
    setSelected(tier);
    setSelectedId(tierId);
    setSelectedBool(true);
    // No need to set selectedCode anymore - we use tier + interval dynamically
    setSuccessMessage(`Congratulations! You are now upgraded to ${tier} status!`);
  };

  const handleAddOnChange = (e) => {
    setSelectedAddOn(e.target.value);
  };

  const handleStripeCheckout = async () => {
    // Always make the backend call, even in localhost mode
    try {
      setSubscribing(true);
      
      // Ensure we have a valid token
      let authToken = token;
      if (typeof authToken === 'function') {
        authToken = await authToken();
      }
      
      if (!authToken || typeof authToken !== 'string' || authToken === 'undefined') {
        toast.error('No valid authentication token found. Please log in again.');
        setSubscribing(false);
        return;
      }

      // Prepare request body using modern tier-based approach
      const requestBody = {
        type: "create_session",
        tier: selectedId,
        interval: subLength === "monthly" ? "monthly" : "annual",
        plan: tierPlans[selectedId],
      };

      // Add addons if selected (modern array format)
      if (selectedAddOn) {
        requestBody.addons = [selectedAddOn];
      }
      
      // Add test mode flag for local development
      if (isLocalhost() && testStripeFlow) {
        requestBody.testStripeFlow = true;
      }
      
      const response = await fetch(API_URL + "/users/v1/subscribe", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const responseJson = await response.json();
        
        // Check if this is dev mode without test flow, or if backend returned no URL
        if ((isLocalhost() && !testStripeFlow) || responseJson.url === null) {
          toast.success("Development mode: Subscription activated!");
          setTimeout(() => {
            setSuccessMessage(`Congratulations! You are now subscribed to ${selected}!`);
            setRedirect(true);
          }, 2000);
        } else {
          // Production mode or test flow: redirect to Stripe Checkout
          window.location.href = responseJson.url;
        }
      } else {
        const errorData = await response.json();
        console.error('Subscription error response:', errorData);
        toast.error(`Failed to create checkout session: ${errorData.message || errorData.error || 'Unknown error'}`);
        setSubscribing(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error("Failed to process checkout. Please try again.");
      setSubscribing(false);
    }
  };

  const toggleLength = () => {
    const newLength = subLength === "monthly" ? "annual" : "monthly";
    const newPrices = newLength === "monthly" ? monthlyPrices : annualPrices;
    
    setSubLength(newLength);
    setPrices(newPrices);
    
    // No need to update selectedCode anymore - tier + interval used dynamically
  };
  const tiers = [
    {
      name: "Starter",
      price: prices[0],
      features: [
        "10 Gigabytes of storage",
        "100 Photos per month",
        "50 E-mails per month",
      ],
      cta: "Try it!",
      id: 1,
      note: "* First month is free! Cancel anytime",
    },
    {
      name: "Basic",
      price: prices[1],
      features: [
        "100 Gigabytes of storage",
        "1000 Photos per month",
        "200 E-mails per month",
      ],
      cta: "Subscribe Now",
      id: 2,
    },
    {
      name: "Plus",
      price: prices[2],
      features: [
        "500 Gigabytes of storage",
        "Unlimited Photos per month",
        "5000 E-mails per month",
      ],
      cta: "Subscribe Now",
      id: 3,
    },
    {
      name: "Premium",
      price: prices[3],
      features: [
        "1500 Gigabytes of storage",
        "Unlimited Photos per month",
        "Unlimited E-mails per month",
      ],
      cta: "Best Value!",
      id: 4,
    },
  ];

  return (
    <>
      <Modal open={subscribing}>
        <Box sx={modalStyle}>
          <Typography variant="h5" component="h2" color="primary">
            Transaction Ongoing...
          </Typography>
          <Typography sx={{ mt: 2 }}>
            {isLocalhost() 
              ? "Development mode: Processing subscription..." 
              : "A new window will pop-up. Please close it when you are done."
            }
          </Typography>
          <Typography sx={{ mt: 3 }} color="text.secondary">
            This will automatically close when you're done.
          </Typography>
          <CircularProgress sx={{ mt: 3 }} />
        </Box>
      </Modal>
      
      <Box sx={{ flexGrow: 1, mt: 5, pt: 5, px: 3 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={10} lg={8}>
            <Box textAlign="center" mb={5}>
              <Typography variant="h4" component="h1" gutterBottom>
                Choose a Subscription that's perfect for your needs
              </Typography>
              {isLocalhost() && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  🚧 Development Mode: Subscriptions will be auto-approved
                </Typography>
              )}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={subLength === "annual"}
                      onChange={toggleLength}
                    />
                  }
                  label="Annual"
                />
                <Typography variant="body2" color="textSecondary">
                  (Save with an annual plan!)
                </Typography>
                
                {/* Developer test mode toggle - only show in localhost */}
                {isLocalhost() && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={testStripeFlow}
                          onChange={(e) => setTestStripeFlow(e.target.checked)}
                          color="warning"
                        />
                      }
                      label="Test Stripe Webhook Flow"
                    />
                    <Typography variant="caption" display="block" color="textSecondary">
                      When enabled: Uses real Stripe checkout with staging webhook processing.<br/>
                      When disabled: Simulates subscription without Stripe (instant activation).
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            
            <Grid container spacing={4}>
              {tiers.map((tier) => (
                <Grid item xs={12} sm={6} md={3} key={tier.id}>
                  <SubscriptionPaper
                    elevation={3}
                    onClick={() => handleSubscription(tier.name, tier.id)}
                    tiercolor={tierColors[tier.id]}
                  >
                    <Box>
                      <Typography variant="h5">{tier.name}</Typography>
                      <Typography variant="h6" color="primary">
                        USD {tier.price}
                      </Typography>
                      <hr />
                      {tier.features.map((feature, index) => (
                        <Typography variant="body2" key={index}>
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                    <Box mt={2}>
                      <Typography
                        variant="button"
                        style={{ color: tierColors[tier.id] }}
                      >
                        {tier.cta}
                      </Typography>
                      {tier.note && (
                        <Typography variant="caption" display="block" mt={1}>
                          {tier.note}
                        </Typography>
                      )}
                    </Box>
                  </SubscriptionPaper>
                </Grid>
              ))}
            </Grid>

            {redirect && (
              <Navigate
                to="/dashboard"
                state={{
                  alertMessage: successMessage,
                  alertType: "success",
                }}
                replace
              />
            )}

            <Collapse in={selectedBool}>
              <Box mt={5} p={3} component={Paper} elevation={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6} sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle1">
                      You have selected
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        p: 1,
                        mt: 1,
                        borderRadius: 1,
                        color: "white",
                        backgroundColor: tierColors[selectedId],
                        fontWeight: "bold",
                      }}
                    >
                      {selected} / {subLength}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle1">
                      {isLocalhost() 
                        ? "Click below to activate subscription (dev mode)" 
                        : "Check-out by clicking the link below."
                      }
                    </Typography>
                    <Box mt={2}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="addon-select-label">
                          Add-on Packages (optional)
                        </InputLabel>
                        <Select
                          labelId="addon-select-label"
                          id="addon-select"
                          value={selectedAddOn || ""}
                          label="Add-on Packages (optional)"
                          onChange={handleAddOnChange}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {addOnOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label} (+${opt.price}/mo)
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={reviseMode ? changeSubscription : handleStripeCheckout}
                        disabled={subscribing}
                        sx={{ mt: 2 }}
                      >
                        {reviseMode 
                          ? (isLocalhost() ? "Update Subscription (Dev)" : "Update Subscription")
                          : (isLocalhost() ? "Subscribe (Dev Mode)" : "Subscribe with Stripe")
                        }
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Subscriptions;
